/**
 * UTM Capture Plugin
 *
 * Captures first-touch attribution data from URL parameters and referrer.
 * Stores in first-party cookie and includes in event context.
 *
 * Captured parameters:
 * - UTM: source, medium, campaign, content, term
 * - Click IDs: gclid (Google), fbclid (Facebook), ttclid (TikTok),
 *              msclkid (Microsoft), li_fat_id (LinkedIn)
 * - Landing page and referrer
 *
 * First-touch attribution is immutable - once captured, it's preserved
 * for the lifetime of the visitor until they reset or clear cookies.
 */

import type { Plugin } from "./plugin.js";
import type { SegmentflowWeb } from "../web.js";
import { getCookieStorage, type CookieOptions } from "../storage/cookie.js";
import type { FirstTouchData } from "@segmentflow/api-schemas";

// Re-export for consumers of this module
export type { FirstTouchData } from "@segmentflow/api-schemas";

// Cookie name for first-touch attribution data
const FIRST_TOUCH_COOKIE = "sf_first_touch";

// Default cookie options (2 years, but Safari ITP limits to 7 days)
const COOKIE_OPTIONS: CookieOptions = {
  expiryDays: 730,
  sameSite: "Lax",
  secure: typeof window !== "undefined" && window.location.protocol === "https:",
};

/**
 * Extract query parameter from URL
 */
function getQueryParam(url: URL, param: string): string | null {
  return url.searchParams.get(param);
}

/**
 * Parse URL and extract attribution parameters
 */
function extractAttributionParams(url: URL): Partial<FirstTouchData> {
  return {
    utm_source: getQueryParam(url, "utm_source"),
    utm_medium: getQueryParam(url, "utm_medium"),
    utm_campaign: getQueryParam(url, "utm_campaign"),
    utm_content: getQueryParam(url, "utm_content"),
    utm_term: getQueryParam(url, "utm_term"),
    gclid: getQueryParam(url, "gclid"),
    fbclid: getQueryParam(url, "fbclid"),
    ttclid: getQueryParam(url, "ttclid"),
    msclkid: getQueryParam(url, "msclkid"),
    li_fat_id: getQueryParam(url, "li_fat_id"),
  };
}

/**
 * Check if any attribution parameters are present
 */
function hasAttributionParams(params: Partial<FirstTouchData>): boolean {
  return Object.values(params).some((v) => v !== null && v !== undefined);
}

/**
 * Get the referrer domain for cleaner storage
 */
function getReferrerDomain(referrer: string): string {
  if (!referrer) return "";
  try {
    const url = new URL(referrer);
    return url.hostname;
  } catch {
    return referrer;
  }
}

/**
 * UTM Capture Plugin Implementation
 */
export class UtmPlugin implements Plugin {
  name = "utm";

  private cookieStorage = getCookieStorage(COOKIE_OPTIONS);
  private firstTouch: FirstTouchData | null = null;

  /**
   * Load plugin and capture first-touch data if not already captured
   */
  load(_analytics: SegmentflowWeb): void {
    // Check if first-touch already captured
    const existingData = this.cookieStorage.getItem(FIRST_TOUCH_COOKIE);
    if (existingData) {
      try {
        this.firstTouch = JSON.parse(existingData) as FirstTouchData;
        return; // Already have first-touch data
      } catch {
        // Invalid data, will re-capture
      }
    }

    // Capture first-touch data from current URL
    this.captureFirstTouch();
  }

  /**
   * Capture first-touch attribution from current page
   */
  private captureFirstTouch(): void {
    if (typeof window === "undefined") return;

    try {
      const url = new URL(window.location.href);
      const params = extractAttributionParams(url);

      // Only capture if we have attribution params OR a referrer
      const referrer = document.referrer || "";
      const referrerDomain = getReferrerDomain(referrer);

      // Skip if referrer is same domain (internal navigation)
      const currentDomain = window.location.hostname;
      const isInternalReferrer =
        referrerDomain === currentDomain ||
        referrerDomain.endsWith(`.${currentDomain}`) ||
        currentDomain.endsWith(`.${referrerDomain}`);

      if (!hasAttributionParams(params) && (!referrer || isInternalReferrer)) {
        // No attribution data to capture (direct visit without tracking params)
        // Still store basic first touch for attribution chain
        this.firstTouch = {
          utm_source: null,
          utm_medium: null,
          utm_campaign: null,
          utm_content: null,
          utm_term: null,
          gclid: null,
          fbclid: null,
          ttclid: null,
          msclkid: null,
          li_fat_id: null,
          landing_page: window.location.pathname,
          referrer: "",
          captured_at: new Date().toISOString(),
        };
      } else {
        this.firstTouch = {
          utm_source: params.utm_source ?? null,
          utm_medium: params.utm_medium ?? this.inferMedium(params, referrer),
          utm_campaign: params.utm_campaign ?? null,
          utm_content: params.utm_content ?? null,
          utm_term: params.utm_term ?? null,
          gclid: params.gclid ?? null,
          fbclid: params.fbclid ?? null,
          ttclid: params.ttclid ?? null,
          msclkid: params.msclkid ?? null,
          li_fat_id: params.li_fat_id ?? null,
          landing_page: window.location.pathname + window.location.search,
          referrer: referrerDomain,
          captured_at: new Date().toISOString(),
        };
      }

      // Persist to cookie
      this.cookieStorage.setItem(
        FIRST_TOUCH_COOKIE,
        JSON.stringify(this.firstTouch),
      );
    } catch (error) {
      // Fail silently - attribution is not critical path
      console.debug("[Segmentflow] Failed to capture first-touch data:", error);
    }
  }

  /**
   * Infer medium from click IDs or referrer
   */
  private inferMedium(
    params: Partial<FirstTouchData>,
    referrer: string,
  ): string | null {
    // Infer from click IDs
    if (params.gclid) return "cpc";
    if (params.fbclid) return "paid_social";
    if (params.ttclid) return "paid_social";
    if (params.msclkid) return "cpc";
    if (params.li_fat_id) return "paid_social";

    // Infer from referrer
    if (!referrer) return null;

    const domain = getReferrerDomain(referrer).toLowerCase();

    // Search engines
    if (
      domain.includes("google.") ||
      domain.includes("bing.") ||
      domain.includes("yahoo.") ||
      domain.includes("duckduckgo.")
    ) {
      return "organic";
    }

    // Social media
    if (
      domain.includes("facebook.") ||
      domain.includes("twitter.") ||
      domain.includes("linkedin.") ||
      domain.includes("instagram.") ||
      domain.includes("tiktok.") ||
      domain.includes("pinterest.") ||
      domain.includes("reddit.")
    ) {
      return "social";
    }

    // Email (common email domains in referrer usually indicate webmail)
    if (
      domain.includes("mail.") ||
      domain.includes("outlook.") ||
      domain.includes("gmail.")
    ) {
      return "email";
    }

    return "referral";
  }

  /**
   * Get first-touch data for inclusion in event context
   */
  getFirstTouch(): FirstTouchData | null {
    return this.firstTouch;
  }

  /**
   * Enrich track events with first-touch context
   */
  track(event: { context?: Record<string, unknown> }): void {
    if (this.firstTouch && event.context) {
      event.context.first_touch_utm = this.firstTouch;
    }
  }

  /**
   * Enrich identify events with first-touch context
   */
  identify(event: { context?: Record<string, unknown> }): void {
    if (this.firstTouch && event.context) {
      event.context.first_touch_utm = this.firstTouch;
    }
  }

  /**
   * Enrich page events with first-touch context
   */
  page(event: { context?: Record<string, unknown> }): void {
    if (this.firstTouch && event.context) {
      event.context.first_touch_utm = this.firstTouch;
    }
  }
}

/**
 * Create UTM plugin instance
 */
export function createUtmPlugin(): Plugin {
  return new UtmPlugin();
}

export default createUtmPlugin;
